import { apiClient } from './client'
import type { AccessControl } from '@/types/domain'

/**
 * A folder as the gateway stores it (`internal/models/folder.go`).
 *
 * Folders are flat — there is no parent on the wire, and no template count;
 * the cockpit counts the templates it has listed. `members` is the role map
 * the Postgres backend keeps (uid → viewer|contributor|manager), and
 * `access_control` the legacy read/write/admin shape a PATCH may still send,
 * which `internal/folders/postgres.go` maps onto those roles.
 */
export interface Folder {
  id: string
  name: string
  members?: Record<string, string>
  access_control?: AccessControl
  documents?: string[]
  created_by?: string
  created_at?: string
  updated_at?: string
}

export const foldersApi = {
  async list(): Promise<Folder[]> {
    const { data } = await apiClient.axios.get<{ folders?: Folder[] }>('/v1/folders')
    return data.folders ?? []
  },

  /** One folder, for the cockpit breadcrumb. The gateway answers 404 both
   *  for a folder that does not exist and for one the caller may not see,
   *  deliberately: which of the two it is, is itself information. */
  async get(folderId: string): Promise<Folder> {
    const { data } = await apiClient.axios.get<Folder>(`/v1/folders/${folderId}`)
    return data
  },

  /** The create endpoint takes a name and nothing else; the creator becomes
   *  the folder's manager. */
  async create(name: string): Promise<Folder> {
    const { data } = await apiClient.axios.post<Folder>('/v1/folders', { name })
    return data
  },

  async patch(
    id: string,
    updates: { name?: string; access_control?: AccessControl },
  ): Promise<void> {
    await apiClient.axios.patch(`/v1/folders/${id}`, updates)
  },

  async remove(id: string): Promise<void> {
    await apiClient.axios.delete(`/v1/folders/${id}`)
  },
}
